/**
 * Morph CT · CT+ 솔루션 상세 페이지 · 영상 분석(비조영 뇌 CT 위축 정량)
 * 내용은 제품팀 소개 페이지(2026-10-23)를 옮긴 것. 그래픽 수치는 예시.
 */
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import {
  CardGrid,
  CompareCards,
  DemoCta,
  DeviceInfo,
  FaqSection,
  FeatureRow,
  Figure,
  Gauge,
  HeroVisual,
  IntegrationSection,
  KeyValues,
  OvLabel,
  OvValue,
  PercentileLegend,
  Pill,
  PercentileRows,
  ProdSection,
  SampleNote,
  ToneLegend,
  TrendChart,
} from '../../components/product-page.js';
import { DEMO_HREF, DEVICE_DISCLAIMER } from '../../lib/product-content.js';

function Page() {
  return (
    <PublicLayout disclaimerItems={DEVICE_DISCLAIMER}>
      <SolutionLayout
        stage="img"
        badgeLabel="IMG · 영상 분석"
        certs={['식약처 신고 D제신 25-38호']}
        domain="뇌 위축 정량 · 비조영 뇌 CT"
        name="Morph CT · CT+"
        tagline="MRI 없이 CT로 보는 뇌 위축"
        description={<><strong>비조영 뇌 CT</strong>로 14개 영역의 위축을 정량합니다. Morph CT+는 3년 내 알츠하이머형 치매 진행 위험도를 더합니다.</>}
        cta={{ label: '데모 신청', href: DEMO_HREF }}
        visual={
          <HeroVisual
            title="Morph CT · Analysis"
            src="/solutions/morph-ct-hero.png"
            alt="Morph CT 분석 결과 화면"
            caption="분석 결과 화면 · 샘플 데이터"
            bl={
              <>
                <OvLabel>CT 위축 순위 · 정상군 100명 기준</OvLabel>
                <div className="prod-ov-row">
                  <OvValue value="84" unit="번째" />
                  <Pill tone="ok">정상 범위</Pill>
                </div>
                <span className="prod-ov-note">MRI 없이 비조영 CT로 분석</span>
              </>
            }
            tr={
              <>
                <OvLabel chip="CT+">3년 진행 위험도</OvLabel>
                <Gauge value={65.9} thresholds={[25, 62]} />
                <OvValue value="65.9" unit="%" tone="alert" />
              </>
            }
          />
        }
        crossSell={[
          { href: '/solutions/amylo', name: 'Amylo', desc: '아밀로이드 PET를 표준 단위 Centiloid로 정량', stage: 'img' },
          { href: '/solutions/morph', name: 'Morph · Morph+', desc: '진료용 MRI로 보는 뇌 위축', stage: 'img' },
          { href: '/solutions/scst', name: 'SCST', desc: '인지 영역별 표준화 점수', stage: 'dx' },
        ]}
        after={
          <>
            <DemoCta name="Morph CT" />
            <DeviceInfo
              keys={['제품명', '모델명', '제품코드(등급)', '인허가', '제조원']}
              columns={[
                {
                  name: 'Morph CT',
                  rows: {
                    제품명: 'Morph CT',
                    모델명: 'MCT',
                    '제품코드(등급)': 'F1ABXA1 (1등급)',
                    인허가: '식약처 디지털의료기기 제조 신고 D제신 25-38호',
                    제조원: '(주)뷰브레인헬스케어',
                  },
                },
                {
                  name: 'Morph CT+',
                  rows: {
                    제품명: 'Morph CT Plus',
                    모델명: 'MCTP',
                    '제품코드(등급)': 'D1ABXA1 (1등급)',
                    인허가: '식약처 디지털의료기기 제조 신고 D제신 26-20호',
                    제조원: '(주)뷰브레인헬스케어',
                  },
                },
              ]}
            />
          </>
        }
      >
        {/* ── 개요 ─────────────────────────────────────────────── */}
        <ProdSection label="Overview" title="MRI 없이도 CT로 뇌 위축을 봅니다">
          <CardGrid
            cols={3}
            items={[
              { title: 'MRI 없이 위축 정량', desc: '비조영 뇌 CT만으로 14개 영역의 위축을 정량합니다.' },
              { title: '정상군 대비 위치', desc: '연령 · 성별 정상군 백분위로 영역별 위축 정도를 보여줍니다.' },
              { title: '3년 진행 위험도 (CT+)', desc: '임상 정보를 더해 3년 내 알츠하이머형 치매 진행 위험도를 제공합니다.' },
            ]}
          />
        </ProdSection>

        {/* ── 주요 기능 ─────────────────────────────────────────── */}
        <ProdSection alt label="Features" title="주요 기능">
          <FeatureRow
            no="01"
            title="14개 영역 위축 순위"
            desc="전두 · 측두 · 두정 · 후두, 측뇌실 각 부위와 해마 인접 영역의 좌 · 우 위축 순위를 보여줍니다."
            tags={['정상군 백분위', '좌 · 우 14개 영역']}
          >
            <Figure head="CT 위축 순위 · 정상군 100명 기준" legend={<PercentileLegend />}>
              <PercentileRows
                rows={[
                  { name: '전두엽', sub: '판단 · 계획 · 집중', value: 45, right: 53, left: 37, tone: 'ok', status: '정상 범위' },
                  { name: '측두엽', sub: '말 이해 · 기억 저장', value: 94, right: 96, left: 89, tone: 'warn', status: '주의' },
                  { name: '두정엽', sub: '감각 · 방향 감각', value: 23, right: 41, left: 11, tone: 'ok', status: '정상 범위' },
                  { name: '후두엽', sub: '시각 처리', value: 27, right: 18, left: 40, tone: 'ok', status: '정상 범위' },
                  { name: '해마 인접', sub: '기억 관련', value: 99, right: 100, left: 88, tone: 'alert', status: '위축 뚜렷' },
                  { name: '뇌실 앞쪽', sub: '뇌가 줄면 커짐', value: 99, right: 100, left: 96, tone: 'alert', status: '위축 뚜렷' },
                  { name: '뇌실 뒤쪽', sub: '뇌가 줄면 커짐', value: 91, right: 96, left: 81, tone: 'warn', status: '주의' },
                ]}
              />
            </Figure>
          </FeatureRow>

          <FeatureRow
            no="02"
            reverse
            title="부피와 위축 추이"
            desc="영역별 CSF 부피와 검사일별 위축 순위 변화를 한 장에서 확인합니다."
            tags={['부피(mL)', '검사일별 추이', '이전 CT 비교']}
          >
            <Figure head="위축 순위 · 검사일별">
              <TrendChart
                points={[
                  { x: '2024.05', y: 78, label: '78번째' },
                  { x: '2025.05', y: 81, label: '81번째' },
                  { x: '2026.05', y: 84, label: '84번째' },
                ]}
                yMin={70}
                yMax={90}
                ticks={[70, 80, 90]}
              />
              <div className="prod-figure-head">영역별 CSF 부피</div>
              <KeyValues
                items={[
                  { k: '전체', v: '238.6', unit: 'mL' },
                  { k: '전두엽', v: '70.3', unit: 'mL' },
                  { k: '측두엽', v: '47.0', unit: 'mL' },
                  { k: '두정엽', v: '41.2', unit: 'mL' },
                  { k: '후두엽', v: '16.5', unit: 'mL' },
                ]}
              />
            </Figure>
          </FeatureRow>

          <FeatureRow
            no="03"
            badge="Morph CT+"
            title="3년 내 알츠하이머형 치매 진행 위험도"
            desc="CT 위축 정량에 성별 · 나이 · 교육년수 · 인지기능검사 결과를 더해 3년 진행 위험도를 저 · 중 · 고로 제공합니다."
            tags={['저 · 중 · 고 구분', '경도인지장애 · 인지저하 의심 환자']}
          >
            <Figure>
              <div className="prod-gauge-split">
                <Gauge value={65.9} thresholds={[25, 62]} size="lg" />
                <div className="prod-gauge-text">
                  <div className="prod-stat-label">3년 진행 위험도</div>
                  <OvValue value="65.9" unit="%" tone="alert" xl />
                  <Pill tone="alert">고위험</Pill>
                  <ToneLegend
                    items={[
                      { tone: 'ok', label: '저 25% 미만' },
                      { tone: 'warn', label: '중 25~62%' },
                      { tone: 'alert', label: '고 62% 초과' },
                    ]}
                  />
                </div>
              </div>
            </Figure>
          </FeatureRow>
          <SampleNote />
        </ProdSection>

        {/* ── 활용 환경 ─────────────────────────────────────────── */}
        <ProdSection label="Use Cases" title="이런 환경에서 활용합니다">
          <CardGrid
            cols={3}
            items={[
              { kicker: '도입 환경', title: 'MRI가 없는 중소병원 · 의원', desc: '보유한 CT 장비로 뇌 위축을 분석합니다.' },
              { kicker: '촬영 경로', title: '응급 · 외래 · 검진 CT', desc: '이미 촬영한 비조영 CT를 그대로 활용합니다.' },
              { kicker: '대상 환자 · CT+', title: '인지저하 의심 · 경도인지장애 환자', desc: 'CT+로 3년 내 알츠하이머형 치매 진행 위험도를 함께 확인합니다.' },
            ]}
          />
        </ProdSection>

        {/* ── Morph CT · CT+ 비교 ───────────────────────────────── */}
        <ProdSection alt label="Morph CT · CT+" title="필요에 맞게 Morph CT 또는 Morph CT+">
          <CompareCards
            items={[
              { name: 'Morph CT', input: '비조영 뇌 CT', output: '14개 영역 부피 · 정상군 백분위 · 과거 비교', foot: '위축 정량' },
              { name: 'Morph CT+', input: '뇌 CT + 성별 · 나이 · 교육년수 · 인지검사', output: '+ 3년 진행 위험도', foot: '위축 정량 + 예측' },
            ]}
          />
        </ProdSection>

        <IntegrationSection />

        <FaqSection
          alt
          items={[
            {
              q: 'MRI가 없는 기관에서도 사용할 수 있나요?',
              a: '네. 비조영 뇌 CT만으로 Morph와 같은 14개 CSF 영역의 부피와 위축 순위를 산출합니다.',
            },
            {
              q: 'Morph CT와 Morph CT+는 어떻게 다른가요?',
              a: 'Morph CT는 14개 영역의 위축을 정량합니다. Morph CT+는 여기에 임상 정보를 더해 3년 내 알츠하이머형 치매 진행 위험도를 함께 제공합니다.',
            },
            {
              q: 'Morph CT+는 어떤 환자에게 사용하나요?',
              a: '인지검사 등으로 인지저하가 의심되거나 경도인지장애로 진단받은 환자에게 사용합니다.',
            },
            {
              q: '분석 결과만으로 진단할 수 있나요?',
              a: '진단이나 치료를 단독으로 결정하는 용도가 아닙니다. 추적관찰 · 관리 계획 수립을 위한 참고 정보로 제공되며, 최종 판단은 담당의 종합 판단에 따릅니다.',
            },
          ]}
        />
      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/morph-ct')({
  component: Page,
});
